import React, { useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import {
  formatValue,
  getLineColor,
  getLegendConfig,
  getYAxisDomain,
  processTimeseriesData
} from './chartUtils';
import WidgetState from './WidgetState';

const BarChartWidget = ({ data, config = {} }) => {
  const theme = useTheme();

  // Memoize processed timeseries data
  const processedData = useMemo(() => {
    if (!data || !data.data || data.type !== 'timeseries') {
      return null;
    }
    return processTimeseriesData(data.data, config.legendTemplate);
  }, [data, config.legendTemplate]);

  // Memoize legend and axis configurations
  const legendConfig = useMemo(() => getLegendConfig(config), [
    config
  ]);

  const yAxisDomain = useMemo(() => getYAxisDomain(config), [
    config
  ]);

  const yAxisLabel = useMemo(() =>
    config.yAxisLabel ? { value: config.yAxisLabel, angle: -90, position: 'insideLeft' } : undefined,
    [config.yAxisLabel]
  );

  // Memoize formatters — timestamps are already in milliseconds from processTimeseriesData
  const xAxisTickFormatter = useCallback(
    (timestamp) => new Date(timestamp).toLocaleTimeString(),
    []
  );

  const yAxisTickFormatter = useCallback(
    (value) => formatValue(value, { ...config, unit: '' }),
    [config]
  );

  const tooltipLabelFormatter = useCallback(
    (timestamp) => new Date(timestamp).toLocaleString(),
    []
  );

  const tooltipFormatter = useCallback(
    (value) => [formatValue(value, config), ''],
    [config]
  );

  // Theme-aware tooltip surface — glassy dark on dark, light bg + dark text on light.
  const tooltipContentStyle = useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    return {
      backgroundColor: isDark ? 'rgba(5, 5, 5, 0.75)' : 'rgba(255, 255, 255, 0.92)',
      backdropFilter: 'blur(12px)',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)',
      borderRadius: '8px',
      boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.15)',
      color: isDark ? '#fff' : '#1a1a1a',
    };
  }, [theme.palette.mode]);

  // Memoize bar chart configurations
  const stackId = useMemo(() =>
    data?.data?.length > 3 ? "stack1" : undefined,
    [data?.data?.length]
  );

  const barSize = useMemo(() =>
    data?.data?.length > 5 ? 15 : 30,
    [data?.data?.length]
  );

  if (!data) {
    return (
      <WidgetState isEmpty />
    );
  }

  // Handle scalar/table results
  if (data.type === 'scalar' || (data.type === 'table' && data.rows)) {
    let scalarValue = null;
    if (data.type === 'scalar') {
      scalarValue = parseFloat(data.data);
    } else if (data.rows.length > 0) {
      const numVal = Object.values(data.rows[0]).find(v => typeof v === 'number');
      if (numVal !== undefined) scalarValue = numVal;
    }
    if (scalarValue !== null) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>{formatValue(scalarValue, config)}</div>
          <div style={{ fontSize: '0.75rem', color: '#999', marginTop: 8 }}>
            Scalar result — use Stat or Gauge widget, or add GROUP BY with a time column for timeseries
          </div>
        </div>
      );
    }
  }

  if (!data.data) {
    return (
      <WidgetState isEmpty />
    );
  }

  if (data.type === 'timeseries' && processedData) {
    if (!processedData.dataPoints || processedData.dataPoints.length === 0) {
      return (
        <WidgetState isEmpty />
      );
    }
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={processedData.dataPoints} syncId="dashboard-sync">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="timestamp"
            type="number"
            domain={config.timeDomain || ['dataMin', 'dataMax']}
            allowDataOverflow={Boolean(config.timeDomain)}
            tickFormatter={xAxisTickFormatter}
          />
          <YAxis
            domain={yAxisDomain}
            label={yAxisLabel}
            tickFormatter={yAxisTickFormatter}
          />
          <Tooltip
            labelFormatter={tooltipLabelFormatter}
            formatter={tooltipFormatter}
            contentStyle={tooltipContentStyle}
            itemStyle={{ fontWeight: 500 }}
            cursor={{ fill: 'rgba(128, 128, 128, 0.12)' }}
          />
          {legendConfig && <Legend {...legendConfig} />}
          {processedData.series.map((series, index) => (
            <Bar
              key={series}
              dataKey={series}
              fill={getLineColor(index, config.colorScheme)}
              stackId={stackId}
              barSize={barSize}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  } else if (data.type === 'histogram') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.data} syncId="dashboard-sync">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" />
          <YAxis
            domain={yAxisDomain}
            label={yAxisLabel}
            tickFormatter={yAxisTickFormatter}
          />
          <Tooltip
            formatter={tooltipFormatter}
            contentStyle={tooltipContentStyle}
            itemStyle={{ fontWeight: 500 }}
            cursor={{ fill: 'rgba(128, 128, 128, 0.12)' }}
          />
          <Bar
            dataKey="value"
            fill={getLineColor(0, config.colorScheme)}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  } else {
    return <div>Unsupported data type for bar chart</div>;
  }
};

export default React.memo(BarChartWidget, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.config?.legendTemplate === nextProps.config?.legendTemplate &&
    prevProps.config?.showLegend === nextProps.config?.showLegend &&
    prevProps.config?.legendPosition === nextProps.config?.legendPosition &&
    prevProps.config?.unit === nextProps.config?.unit &&
    prevProps.config?.decimalPlaces === nextProps.config?.decimalPlaces &&
    prevProps.config?.useThousandSeparator === nextProps.config?.useThousandSeparator &&
    prevProps.config?.yAxisMin === nextProps.config?.yAxisMin &&
    prevProps.config?.yAxisMax === nextProps.config?.yAxisMax &&
    prevProps.config?.yAxisLabel === nextProps.config?.yAxisLabel &&
    prevProps.config?.colorScheme === nextProps.config?.colorScheme &&
    prevProps.config?.thresholdsJson === nextProps.config?.thresholdsJson &&
    (prevProps.config?.timeDomain?.[0] === nextProps.config?.timeDomain?.[0] &&
     prevProps.config?.timeDomain?.[1] === nextProps.config?.timeDomain?.[1])
  );
});